import type { ResolvedStepAnswer } from '@factorialco/f0-react'

/**
 * Arguments to persist the resolution of a clarifying question tool call.
 * Posts to the backend so the tool's args include `isResolved: true` and the
 * captured answers, which makes the render component return null on
 * subsequent history loads.
 */
export interface PersistClarifyingResolutionParams {
  chatApiEndpoint: string
  headers?: Record<string, string>
  threadId: string
  toolCallId: string
  runtimeFetch?: typeof fetch
  args: {
    steps: unknown
    isResolved: true
    answers: ResolvedStepAnswer[]
    /**
     * Set to `true` when the user cancelled the flow with Escape. The
     * tool call is still marked as resolved so the render is a no-op on
     * reload, but the flag lets the agent distinguish "answered" from
     * "cancelled without answering".
     */
    cancelled?: boolean
  }
}

/**
 * Fire-and-forget PATCH to mark a ClarifyingQuestion tool call as resolved.
 * Failures are swallowed — if persistence fails the user still moves forward;
 * worst case the panel reappears on the next history load.
 */
export async function persistClarifyingResolution({
  chatApiEndpoint,
  headers,
  threadId,
  toolCallId,
  args,
  runtimeFetch = fetch,
}: PersistClarifyingResolutionParams): Promise<void> {
  try {
    await runtimeFetch(
      `${chatApiEndpoint}/chat-history/threads/${encodeURIComponent(
        threadId
      )}/tool-calls/${encodeURIComponent(toolCallId)}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(headers ?? {}),
        },
        credentials: 'include',
        body: JSON.stringify({ args }),
      }
    )
  } catch (error) {
    // Non-fatal: best-effort persistence.
    // eslint-disable-next-line no-console
    console.warn('[ClarifyingQuestion] Failed to persist resolution', error)
  }
}
